

import { EntityManagerRegistrar } from "./EntityManagerRegistrar";
import { GetCorrelation, GLError, ICorrelatedEvent, ICorrelation } from "../Utilities";
import { BaseViewModel, IEntityConstructorOptions } from "../Base/BaseViewModel";
import { HydrationBase } from "./HydrationBase";
import { Nullable } from "primereact/ts-helpers";
import { IResourceDescriptor, Models, DataEntityBase } from "../../Services/DataService/Model";
import { INotifyPropertyChanged, EventHookManager } from "../../Utilities";
import { EntityKey, EntityId } from "../../Services/DataService/Model/Base/Models";


// export interface IHaveItems<T> {
//     CreateItem(correlation: ICorrelation): Promise<T>;
//     AddItem(correlation: ICorrelation, item: T): void;
//     RemoveItem(correlation: ICorrelation, item: T): void;
//     DeleteItem(correlation: ICorrelation, item: T): Promise<void>;
//     SwapItems(correlation: ICorrelation, index1: number, index2: number): void;
//     values: T[];
// }

export interface IManageEntities<EntityType extends BaseViewModel> {
    CreateItem: (correlation: ICorrelation, options?: IEntityConstructorOptions) => Promise<EntityType>;
    GetItemByKey: (correlation: ICorrelation, key: EntityKey) => Promise<Nullable<EntityType>>;
    GetItemById: (correlation: ICorrelation, id: EntityId) => Promise<Nullable<EntityType>>;
    GetAllItems: (correlation: ICorrelation, fromCache?: boolean) => Promise<EntityType[]>;
    DeleteItem: (correlation: ICorrelation, item: EntityType) => void;
}

export interface IHaveItems<T> extends INotifyPropertyChanged {
    values: T[];
}

export interface IHaveManagedItems<T> extends IHaveItems<T> {
    CreateItem(correlation: ICorrelation): Promise<T>;
    AddItem(correlation: ICorrelation, item: T): void;
    RemoveItem(correlation: ICorrelation, item: T): void;
    DeleteItem(correlation: ICorrelation, item: T): Promise<void>;
    SwapItems(correlation: ICorrelation, index1: number, index2: number): void;
    values: T[];
}


export interface IManageKeys {
    getKeys: () => string[];
    setKeys: (keys: string[]) => void
}

export interface IManageIds {
    getDescriptors: () => IResourceDescriptor[];
    setDescriptors: (keys: IResourceDescriptor[]) => void;
}

export interface IManageItems<T> {
    getItems: () => Promise<T[]>;
    setItems: (items: T[]) => void;
    createFN: (correlation: ICorrelation, name?: string) => Promise<T>;
    compareFN: (item1: T, item2: T) => boolean;
}


export class ItemsManager<T> extends HydrationBase implements IHaveManagedItems<T>, INotifyPropertyChanged {

    constructor(
        options: {
            typeName: Models,
            idMGR?: IManageKeys,
            descriptorMGR?: IManageIds,
            itemMGR?: IManageItems<T>,
            removeNotFound?: boolean
        }
    ) {
        super();

        this._typeName = options.typeName;
        this._keyMGR = options.idMGR;
        this._descriptorMgr = options.descriptorMGR;
        this._itemMgr = options.itemMGR;

        if (!this._descriptorMgr && !this._keyMGR && !this._itemMgr)
            throw new GLError(
                {
                    name: 'constructor.No_KeyMgrOrDescriptorMgr',
                    id: '765adcc6-20d3-41e1-b66b-08c0ab6e2955',
                    telemetryMgr: ItemsManager.telemetryManager
                }, 'Must specify either a keyMGR or a descriptorMGR in options.');
        else if (this._descriptorMgr && this._keyMGR)
            throw new GLError(
                {
                    name: 'constructor.CannotSpecifyDescriptor&KeyMGR',
                    id: 'c0db3fcc-3c1a-4e4b-9ee1-e2e661ef5873',
                    telemetryMgr: ItemsManager.telemetryManager
                }, 'Cannot specify both a descriptorMgr and a keyMgr');
        else if (this._keyMGR && this._itemMgr)
            throw new GLError(
                {
                    name: 'constructor.CannotSpecifyKeyMgr&JSON',
                    id: '10497e80-1a8d-42f4-8a27-030793f355fe',
                    telemetryMgr: ItemsManager.telemetryManager
                }, 'Cannot specify both a keyMgr and a jsonMgr');
        else if (this._itemMgr && this._descriptorMgr)
            throw new GLError(
                {
                    name: 'constructor.CannotSpecifyJson&DescriptorMgr',
                    id: 'cd418474-22b6-49cb-b518-75d64df019dc',
                    telemetryMgr: ItemsManager.telemetryManager
                }, 'Cannot specify both a jsonMgr and a descriptorMgr');


        let entityMgr = EntityManagerRegistrar.GetManager(this._typeName);
        this._createFN = this._itemMgr ?
            this._itemMgr.createFN : async (correlation: ICorrelation, _name?: string) => {

                let createFN = entityMgr!.CreateItem;
                return (await createFN(correlation)) as unknown as Promise<T>
            };


        //
        // TODO: This is a temporary fix.  Need to relearn what case this was trying to cover.
        //
        // //
        // // We need to listen for changes and rebuild the list of ActionItems if new items
        // // are created/deleted except when we are managing a subproperty directly.
        // //
        // if (!this._itemMgr && entityMgr)
        //     User.Instance.OnDeleteItem.Subscribe(this._handleDeletedItem);
    }


    public get ItemsRemoved() { return this._removedEventMgr.eventHook; }



    public async CreateItem(correlation: ICorrelation, itemName?: string) {

        let Track = ItemsManager.telemetryManager?.GetTimedEventTracker('CreateItem', correlation);
        let retVal = await this._createFN(correlation, itemName) as T;

        if (!retVal)
            throw new GLError(
                {
                    name: 'CreateItem',
                    id: '638a5e4c-5415-4b9f-9b6f-ca65b8a3f4dc',
                    telemetryMgr: ItemsManager.telemetryManager
                }, `Failed to create a new ${this._typeName}.`);

        this.values = [...this.values, retVal];
        Track && Track();
        return retVal;
    }


    public AddItem = (correlation: ICorrelation, item: T) => this.AddItems(correlation, [item]);
    public AddItems = (correlation: ICorrelation, items: T[]) => {

        let Track = ItemsManager.telemetryManager?.GetTimedEventTracker('AddItems', correlation);
        this.values = [...this.values, ...items];
        Track && Track();
    }


    public RemoveItem = (correlation: ICorrelation, item: T) => this.RemoveItems(correlation, [item]);
    public RemoveItems = (correlation: ICorrelation, items: T[]) => {


        let Track = ItemsManager.telemetryManager?.GetTimedEventTracker('RemoveItems', correlation, { count: items.length });
        let tempValues: T[] = [];
        tempValues = this.values.filter((i) => !items.includes(i));

        if (tempValues.length !== this.values.length) {
            this.values = tempValues;
            this._removedEventMgr.Fire({ source: this, correlation: correlation });
        }
        Track && Track();
    }
    // private RemoveItemByDescriptor(correlation: ICorrelation, descriptor: IResourceDescriptor) {

    //     let Track = ItemsManager.telemetryManager?.GetTimedEventTracker('RemoveItemByDescriptor', correlation, { descriptor: descriptor });
    //     let tempValues: T[] = [];
    //     tempValues = this.values.filter((item) => {
    //         return !CompareDescriptors((item as unknown as BaseViewModel).Data.descriptor, descriptor)
    //     });

    //     if (tempValues.length !== this.values.length) {
    //         this.values = tempValues;
    //         this._removedEventMgr.Fire({ source: this, correlation: correlation });
    //     }
    //     Track();
    // }


    public DeleteItem = async (correlation: ICorrelation, item: T) => this.DeleteItems(correlation, [item]);
    public DeleteItems = async (correlation: ICorrelation, items: T[]) => {

        let Track = ItemsManager.telemetryManager?.GetTimedEventTracker('DeleteItems', correlation, { count: items.length });

        this.values = this.values.filter((i) => !items.includes(i));

        items.map(async (i) => {
            if (!this._itemMgr) {
                let de = i as unknown as BaseViewModel;
                if (de.Data) {
                    await de.Delete(correlation);
                }
                else {
                    throw new GLError(
                        {
                            name: 'DeleteItems',
                            id: '9a4602bc-019c-4217-812d-d6dc71a968da',
                            telemetryMgr: ItemsManager.telemetryManager
                        }, 'Expected a DataEntityBase, but missing expected properties');
                }
            }
        });
        this._removedEventMgr.Fire({ source: this, correlation: correlation });
        Track && Track();
    }

    public async DeleteAll(correlation: ICorrelation) {

        let Track = ItemsManager.telemetryManager.GetTimedEventTracker('DeleteAll', correlation);

        //
        // There is nothing to do if we don't have any items.
        //
        if (this.values.length === 0)
            return;

        //
        // Loop through and delete the items
        //
        for (let item of this.values) {
            if (!this._itemMgr) {
                let de = item as unknown as BaseViewModel;
                if (de.Data)
                    de.Delete(correlation);
            }
        }

        // Clear our items and fire changed.
        //
        this.values = [];
        Track && Track();
    }

    public SwapItems(correlation: ICorrelation, index1: number, index2: number): void {


        let Track = ItemsManager.telemetryManager.GetTimedEventTracker('SwapItems', correlation);

        let tempList = [...this.values]
        let tempItem = tempList[index1];
        tempList[index1] = tempList[index2];
        tempList[index2] = tempItem;
        this.values = tempList;
        Track && Track();
    }

    public get values() {
        if (!this.isHydrated) {
            this._isJITHydrate = true;
            this.Hydrate(GetCorrelation())
            return [];
        }
        return this._items;
    }


    public set values(newValues) {


        //
        // If we are being set to ourselves (for the effect of creating a new list),
        // make sure that we don't spam the server with useless updates.
        //
        if (newValues !== this.values) {

            if (this._keyMGR) {
                let keys = newValues.map((item) => {
                    return ((item as unknown as BaseViewModel).Data
                        ?? (item as unknown as DataEntityBase)).key!;
                })
                this._keyMGR.setKeys(keys);
            }
            else if (this._descriptorMgr) {
                let descriptors = newValues.map((item) => {
                    var data = (item as unknown as BaseViewModel).Data
                        ?? (item as unknown as DataEntityBase);
                    return data.descriptor;
                })
                this._descriptorMgr.setDescriptors(descriptors);
            }
            else if (this._itemMgr) {
                this._itemMgr.setItems(newValues);
            }
            else {
                throw new GLError(
                    {
                        name: 'values',
                        id: 'f8efcd60-7005-4c21-af5c-a168c94c92e5',
                        telemetryMgr: ItemsManager.telemetryManager
                    }, 'No supported MGR to handle the operations');
            }
        }
        else {
            console.warn('only creating a new list');
        }

        //
        // Do the actual set while generating a new list
        //
        this._items = [...newValues];
        this.FireChanged('values');
    }







    public Includes(id: string) {
        return this._keyMGR?.getKeys().includes(id) ?? false;
    }







    protected async doHydrate(correlation: ICorrelation): Promise<void> {

        let Track = ItemsManager.telemetryManager?.GetTimedEventTracker('Hydrate', correlation);

        //
        // Set up variables
        //
        let healList = false;
        let tempItems: T[] = [];
        let descriptors: IResourceDescriptor[] | undefined = undefined;

        //
        // Depending on which type of manager we have, either get the descriptors
        // or get the items themselvers
        //
        if (this._keyMGR) {
            let tempKeys = this._keyMGR.getKeys();
            descriptors = tempKeys.map((item) => { return { typeName: this._typeName, id: item }; });

        }
        else if (this._descriptorMgr) {
            descriptors = this._descriptorMgr.getDescriptors();
        }
        else if (this._itemMgr) {
            tempItems = await this._itemMgr.getItems();
        }
        else {
            throw new GLError(
                {
                    name: 'Hydrate',
                    id: '06eb3cd4-45c5-45de-a91d-12a73effb57d',
                    telemetryMgr: ItemsManager.telemetryManager
                }, 'No supported MGR to handle the operations');
        }

        //
        // If we get descriptors, loop through and get the items.  If an item has been
        // deleted, mark that we need to do some healing.
        //
        if (descriptors) {
            for (let key of descriptors) {
                try {

                    let getFN = EntityManagerRegistrar.GetManager(key.typeName)!.GetItemByKey;

                    let temp = await getFN(correlation, key.id) as unknown as T;

                    if (temp) {
                        tempItems.push(temp);
                    }
                    else {
                        healList = true;
                    }
                }
                catch (e) {
                    healList = true;
                }
            }
        }

        //
        // We only need to set the values if we are healing the list, otherwise
        // simply setting the _items is sufficient (since there are no changes to 
        // push back to the server.)
        //
        this.SetHydrated();
        if (healList) {
            let properties = {
                typename: this._typeName,
                descriptorCount: descriptors ? descriptors.length : undefined,
                valueCount: this.values.length
            };
            ItemsManager.telemetryManager?.TraceEvent('HydrateHeal', correlation, properties);
            this.values = tempItems;
        }
        else {
            this._items = tempItems;
            if (this._isJITHydrate) {
                this._isJITHydrate = false;
                this.FireChanged('values');
            }
        }
        Track && Track();
    }

    //
    // TODO: Decide if this is dead code. Is related to code at end of constructor.
    //
    // // private _handleDeletedItem = (args: IItemDeleted) => {

    // //     this.RemoveItemByDescriptor(args.correlation, args.descriptor);
    // // }

    private _createFN: (correlation: ICorrelation, name?: string) => Promise<T>;
    private _typeName: Models = Models.Base;
    private _keyMGR?: IManageKeys;
    private _descriptorMgr?: IManageIds;
    private _itemMgr?: IManageItems<T>;
    private _items: T[] = [];
    private _isJITHydrate = false;
    private _removedEventMgr = new EventHookManager<ICorrelatedEvent>();
    private static telemetryManager: any | undefined = undefined;  // TODO: This is a temporary fix.
}